import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "What to Do When Your Support Raising Plateaus at 70-80%";
const DESCRIPTION =
    "Getting from zero to most of the way there is often the easy part. Why support raising stalls just short of full support, and a practical plan for closing the last 20-30%.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover13.jpg',
            datePublished: '2026-08-05',
            dateModified: '2026-08-05',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/what-to-do-when-support-raising-plateaus',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Why does support raising slow down after reaching 70-80%?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Your first donors are usually your closest relationships — family, close friends, your home church — and they say yes quickly. The remaining 20-30% usually has to come from a wider, less-warm circle, which takes more individual asks per yes. The plateau isn't a sign something is wrong; it's a sign you've exhausted the easiest layer of your network and need a different approach for the next one.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I ask my existing donors for more, or find new donors, when I plateau?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Do both, but in a specific order: ask existing donors for warm introductions before asking them for larger gifts. A current supporter introducing you to someone they know converts far better than a cold ask, and it doesn't put your existing relationships in the position of being asked twice in the same season.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How long is it normal to stay stuck at a support-raising plateau?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'There is no universal number, but if you have gone more than 4-6 weeks without a new commitment despite actively asking, that is a signal to change your approach rather than simply waiting or repeating the same appeals to the same people.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is it normal to feel discouraged when support raising stalls?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, and it is one of the most common seasons missionaries describe as the hardest part of support raising — precisely because it comes after real early progress. Treat the plateau as a strategy problem to solve, not a verdict on your calling or your ministry.",
                    },
                },
            ],
        },
    ],
};

export default function WhatToDoWhenSupportRaisingPlateaus() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/what-to-do-when-support-raising-plateaus' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-plateau-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover13.jpg" alt="A group of supporters putting their hands together" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>The first 70-80% of a support goal tends to come together faster than most missionaries expect — family says yes, your home church says yes, close friends say yes. Then, often abruptly, the new commitments slow to a trickle and weeks go by with no movement. This plateau is one of the most common and most discouraging phases of support raising, and it is also one of the most predictable and solvable.</p>

                    <h2>Why the last 20-30% is a different problem than the first 70%</h2>

                    <p>Your earliest supporters are usually your warmest relationships — people who already know you, trust you, and were likely to say yes as soon as you asked. That layer of your network is finite, and once it's tapped, the remaining gifts need to come from people further out: friends of friends, acquaintances, businesses, and donors you haven't met yet. Reaching them takes a different set of moves than reaching your inner circle did, which is exactly why the same approach that worked for the first 70% often stalls on the last 30%.</p>

                    <h2>Step 1: Audit who you have actually asked</h2>

                    <p>Before changing your strategy, check your list against reality. It's common to discover that most of your "no new commitments in weeks" stretch is really "no new asks in weeks" — momentum quietly turned into maintenance mode. List everyone you've asked directly, and be honest about how many people in your broader circle (extended family, coworkers, small group, neighbors) you haven't approached at all yet.</p>

                    <h2>Step 2: Ask current donors for introductions, not just increases</h2>

                    <p>Your existing supporters are your highest-leverage asset for reaching the next layer of your network — often more valuable as connectors than as sources of a bigger gift. A specific ask like, "Is there anyone in your life you think would want to hear about what we're doing?" turns one relationship into a warm introduction instead of a cold ask, and avoids the awkwardness of asking the same person for more money twice in one season. Save the <a href="/resources/how-to-ask-for-a-support-increase">support-increase conversation</a> for existing donors whose situation has clearly changed, not as your default plateau strategy.</p>

                    <h2>Step 3: Widen the circle deliberately</h2>

                    <p>If you've been relying on word-of-mouth and one-on-one meetings, a plateau is a signal to add channels rather than repeat the same one harder:</p>

                    <ul>
                        <li><a href="/resources/how-to-ask-a-business-to-sponsor-your-mission-trip">Approach a local business</a> for a one-time or recurring sponsorship tied to a specific, real cost.</li>
                        <li>Ask your church's missions committee whether there are other churches or small groups in your denomination or network who might want to hear your story.</li>
                        <li>Use <a href="/resources/how-to-ask-for-support-over-text-and-social-media">a public social post followed by individual outreach</a> to re-surface your need to people who may have lost track of where you are in the process.</li>
                    </ul>

                    <h2>Step 4: Re-check your case, not just your list</h2>

                    <p>If asks are going out but conversion is low, the issue may be the pitch rather than the audience. Revisit your <a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">fundraising page bio</a> and your <a href="/resources/how-to-write-a-missionary-support-letter">support letter</a> — a vague calling story or an unclear ask amount quietly costs conversions at every stage, and it costs more at the plateau stage because these are colder relationships with less built-in trust to carry a weak ask.</p>

                    <h2>Step 5: Set a re-evaluation point, not an open-ended wait</h2>

                    <p>Give any new approach 4-6 weeks of consistent effort before judging it. If that window passes with no new commitments despite real activity, that's the signal to change tactics again — try a different channel, ask a different layer of your network, or get a second set of eyes on your pitch — rather than continuing to wait for the plateau to resolve on its own.</p>

                    <h2>It's a strategy problem, not a verdict</h2>

                    <p>A plateau after strong early progress is one of the most common seasons missionaries describe as discouraging, precisely because the early momentum made it feel like the rest would come just as easily. It rarely reflects on your calling or the value of your ministry — it reflects that your easiest-to-reach network is exhausted and the next layer needs a deliberate plan, not just more waiting.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Why does support raising slow down after 70-80%?</strong><br />
                    Your warmest relationships say yes first; the remaining gifts come from a wider circle that takes more deliberate effort to reach.</p>

                    <p><strong>Should I ask existing donors for more, or find new donors?</strong><br />
                    Ask existing donors for introductions to their network before asking them for bigger gifts — it reaches new people without over-asking current supporters.</p>

                    <p><strong>How long is it normal to be stuck at a plateau?</strong><br />
                    If 4-6 weeks pass with no new commitments despite active asking, that's a signal to change your approach.</p>

                    <p><strong>Is it normal to feel discouraged when support raising stalls?</strong><br />
                    Yes — it's one of the most commonly described hard seasons. Treat it as a strategy problem to solve, not a reflection on your calling.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/raising-support-with-no-network">How to Raise Missionary Support When You Don't Have a Big Network</a></li>
                        <li><a href="/resources/common-mistakes-missionary-support-raising">10 Common Mistakes Missionaries Make When Raising Support</a></li>
                        <li><a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">How to Write a Fundraising Page Bio That Gets People to Give</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this after hearing the same story from missionary after missionary: strong momentum to 70-80%, then weeks of silence that felt far worse than it actually was.</p>
                    <p><small><em>Last updated: 2026-08-05.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/what-to-do-when-support-raising-plateaus" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
